import { gql, NetworkStatus, useQuery } from '@apollo/client'
import { useEffect, useMemo } from 'react'

import { currentArtistVar } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  FavoriteMemberFragmentDoc,
  FollowArtistsFragmentDoc,
  MyPreSaleEventConnectionFragmentDoc,
  MyProfileQuery,
  MyProfileQueryVariables,
  OrganizationArtistRankingsConnectionFragmentDoc,
  PatronDetailFragmentDoc,
  RankingStatus,
  UserConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { getActiveMembershipSubscriptionProductPlan } from '@/core/membership'
import { useSettingMyFavoriteModal } from '@/core/myFavorite/useSettingMyFavoriteModal'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { userClaims, user: currentUser } = useCurrentUser()
  const { showModal } = useSettingMyFavoriteModal()

  const { data, loading, fetchMore, networkStatus } = useQuery<
    MyProfileQuery,
    MyProfileQueryVariables
  >(QUERY, {
    variables: {
      artistId: config.artistId,
      fetchMyPreSaleEvents: false,
      isStandalone: config.applicationType === 'standalone',
    },
    notifyOnNetworkStatusChange: true,
    async onCompleted({ myPreSaleEvents, artist, user }) {
      if (artist) {
        currentArtistVar(artist)
      }
      // fetch more item if data is empty and have next page
      if (
        !myPreSaleEvents?.edges?.length &&
        myPreSaleEvents?.pageInfo.hasNextPage
      ) {
        await fetchMore({
          variables: {
            artistId: config.artistId,
            fetchMyPreSaleEvents: true,
            pageInput: { cursor: myPreSaleEvents.pageInfo.endCursor },
            isStandalone: config.applicationType === 'standalone',
          },
        })
      }
    },
  })
  const isNextFetch = !!data
  // 会員番号があるメンバーシップを表示
  const displayMembership = useMemo(() => {
    return data?.user?.organizationPatrons.filter(patron => {
      if (patron.formattedMembershipNumber) {
        return true
      }
      return false
    })
  }, [data?.user?.organizationPatrons])

  const patrons = useMemo(() => {
    const patrons =
      data?.user?.organizationPatrons.flat().filter(item => item) ?? []

    return patrons
      .map(patron => {
        return getActiveMembershipSubscriptionProductPlan(
          patron.artist,
          patron.subscription?.planId ?? ''
        )
      })
      .filter(product => product != null)
  }, [data?.user?.organizationPatrons])

  useEffect(() => {
    if (networkStatus === NetworkStatus.ready) {
      // 推しメンバー設定が必要な場合はモーダルを表示
      if (data?.user) {
        const requiredFavoriteMemberPatron = data.user.organizationPatrons.find(
          patron => patron.requiredFavoriteMember
        )
        if (requiredFavoriteMemberPatron) {
          showModal({
            requiredFavoriteMemberArtistId:
              requiredFavoriteMemberPatron.artistId,
          })
        }
      }
    }
  }, [networkStatus, data?.user])

  return {
    state: {
      loading: loading && !isNextFetch,
      fetchingMyPreSaleEvents:
        networkStatus === NetworkStatus.setVariables ||
        networkStatus === NetworkStatus.fetchMore,
      currentUser: currentUser ?? null,
      user: data?.user ?? null,
      isCustom: !!(
        data?.user?.customProfile?.avatarItem ||
        data?.user?.customProfile?.wallpaperItem
      ),
      prepaidPointBalance: data?.prepaidPointBalance ?? null,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      artistRankings:
        data?.organizationArtistRankings?.edges.filter(
          ({ node }) => node.status === RankingStatus.Active
        ) ?? null,
      events: data?.myPreSaleEvents ?? null,
      membership: displayMembership ?? [],
      favoriteMembers: data?.user?.organizationPatrons ?? [],
      patrons,
    },
    actions: {},
  }
})

const QUERY = gql`
  query MyProfile(
    $artistId: ID
    $pageInput: PageInput
    $fetchMyPreSaleEvents: Boolean!
    $isStandalone: Boolean!
  ) {
    user @skip(if: $fetchMyPreSaleEvents) {
      ...UserConnection
      organizationFollowees {
        ...FollowArtists
      }
      organizationPatrons {
        ...PatronDetail
        ...FavoriteMember
      }
    }
    artist(id: $artistId) @include(if: $isStandalone) {
      ...ArtistBottomTab
      currentLive {
        id
      }
    }
    prepaidPointBalance @skip(if: $fetchMyPreSaleEvents) {
      prepaidPointId
      balance
    }
    organizationArtistRankings @skip(if: $fetchMyPreSaleEvents) {
      ...OrganizationArtistRankingsConnection
    }
    myPreSaleEvents(filter: all, pageInput: $pageInput) {
      ...MyPreSaleEventConnection
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${UserConnectionFragmentDoc}
  ${FollowArtistsFragmentDoc}
  ${OrganizationArtistRankingsConnectionFragmentDoc}
  ${MyPreSaleEventConnectionFragmentDoc}
  ${PatronDetailFragmentDoc}
  ${FavoriteMemberFragmentDoc}
`
